import { toast } from "@/src/common/snackbar"
import { getApiErrorInfo } from "@/src/utils/api-error"
import type { WorkspaceRoleValue } from "@/src/features/internalUsers/constants"

import { apiUrls, httpClient as axiosInstance } from "../api"

function showErrorToast(error: unknown, fallback: string) {
    const { message } = getApiErrorInfo(error, fallback)
    toast.error(message)
}

export const getInternalMe = async () => {
    try {
        const response = await axiosInstance.get(apiUrls.ADMIN_ME())
        return response.data
    } catch (error) {
        console.error("Error getting internal /me:", error)
        showErrorToast(error, "Failed to get user information")
        throw error
    }
}

export type GetInternalWorkspacesParams = {
    page?: number
    size?: number
    search?: string
}

export const getInternalWorkspaces = async (params: GetInternalWorkspacesParams = {}) => {
    try {
        const { page = 0, size = 20, search } = params
        const response = await axiosInstance.get(apiUrls.ADMIN_WORKSPACES(), {
            params: { page, size, ...(search && { search }) },
        })
        return response.data
    } catch (error) {
        console.error("Error getting internal workspaces:", error)
        showErrorToast(error, "Failed to fetch workspaces")
        throw error
    }
}

export type CreditsAddons = {
    pulse?: number
    proctoring?: boolean
    cefr?: boolean
}

export type CreateWorkspaceCredits = {
    isEnabled: boolean
    totalCredits: number
    addons?: CreditsAddons
}

export type CreateInternalWorkspacePayload = {
    workspaceName: string
    websiteLink?: string
    aboutCompany?: string
    audioCredits: CreateWorkspaceCredits
    videoCredits: CreateWorkspaceCredits
    outboundAudioCredits: CreateWorkspaceCredits
    chargeAttemptRule: string
    maxAttempts: number
}

export const createInternalWorkspace = async (payload: CreateInternalWorkspacePayload, logoFile?: File | null) => {
    try {
        const formData = new FormData()
        formData.append("request", new Blob([JSON.stringify(payload)], { type: "application/json" }))
        if (logoFile) {
            formData.append("logo", logoFile)
        }
        const response = await axiosInstance.post(apiUrls.ADMIN_WORKSPACES(), formData)
        return response.data
    } catch (error) {
        console.error("Error creating internal workspace:", error)
        showErrorToast(error, "Failed to create workspace")
        throw error
    }
}

/** PATCH workspace outbound audio (internal). Request: { enabled: boolean }. */
export const patchWorkspaceOutboundAudio = async (
    workspaceId: string,
    enabled: boolean
): Promise<{ data?: any }> => {
    try {
        const response = await axiosInstance.patch(apiUrls.ADMIN_WORKSPACE_OUTBOUND_AUDIO(workspaceId), {
            enabled,
        })
        return response.data
    } catch (error) {
        console.error("Error updating workspace outbound audio:", error)
        showErrorToast(error, "Failed to update outbound audio")
        throw error
    }
}

/** PATCH workspace CSV column catalog generation (internal). Request: { enabled: boolean }. */
export const patchWorkspaceGenerateCsvColumnCatalog = async (
    workspaceId: string,
    enabled: boolean
): Promise<{ data?: unknown }> => {
    try {
        const response = await axiosInstance.patch(
            apiUrls.ADMIN_WORKSPACE_GENERATE_CSV_COLUMN_CATALOG(workspaceId),
            { enabled }
        )
        return response.data
    } catch (error) {
        console.error("Error updating workspace CSV column catalog:", error)
        showErrorToast(error, "Failed to update CSV column catalog")
        throw error
    }
}

/** PATCH workspace interview scheduling (internal). Request: { enabled: boolean }. */
export const patchWorkspaceInterviewScheduling = async (
    workspaceId: string,
    enabled: boolean
): Promise<{ data?: unknown }> => {
    try {
        const response = await axiosInstance.patch(apiUrls.ADMIN_WORKSPACE_INTERVIEW_SCHEDULING(workspaceId), {
            enabled,
        })
        return response.data
    } catch (error) {
        console.error("Error updating workspace interview scheduling:", error)
        showErrorToast(error, "Failed to update interview scheduling")
        throw error
    }
}

export type CreateUserPayload = {
    email: string
    name: string
    password: string
    role: WorkspaceRoleValue
}

export const createUser = async (workspaceId: string, payload: CreateUserPayload) => {
    try {
        const response = await axiosInstance.post(apiUrls.ADMIN_WORKSPACE_USERS(workspaceId), payload)
        return response.data
    } catch (error) {
        console.error("Error creating internal user:", error)
        showErrorToast(error, "Failed to create user")
        throw error
    }
}

// ----------------------------------------------------------------------
// Get all users (internal) - response types
// ----------------------------------------------------------------------

export type InternalUserApi = {
    userId: string
    fullName: string
    email: string
    phoneNumber: string
    status: string
    createdAt: string
    profileImageUrl?: string
}

export type InternalAllUsersResponseData = {
    totalElements: number
    totalPages: number
    first: boolean
    last: boolean
    size: number
    number: number
    content: InternalUserApi[]
    numberOfElements: number
    empty: boolean
    sort?: { empty: boolean; sorted: boolean; unsorted: boolean }
    pageable?: Record<string, unknown>
}

export type InternalAllUsersResponse = {
    statusCode: number
    status: string
    message: string
    data: InternalAllUsersResponseData
}

export const getAllUsers = async (): Promise<InternalAllUsersResponse> => {
    try {
        const response = await axiosInstance.get(apiUrls.ADMIN_ALL_USERS())
        return response.data
    } catch (error) {
        console.error("Error getting all users:", error)
        showErrorToast(error, "Failed to fetch users")
        throw error
    }
}

// ----------------------------------------------------------------------
// Get workspace users - response types (list of users in workspace(s))
// ----------------------------------------------------------------------

export type InternalWorkspaceUserApi = {
    userId: string
    fullName: string
    email: string
    phoneNumber: string
    status: string
    workspaceId: string
    workspaceName: string
    role: string
    isActiveInWorkspace: boolean
    createdAt: string
}

export type InternalWorkspaceUsersResponse = {
    statusCode: number
    status: string
    message: string
    data: InternalWorkspaceUserApi[]
}

export const getWorkspacesUsers = async (workspaceId: string): Promise<InternalWorkspaceUsersResponse> => {
    try {
        const response = await axiosInstance.get(apiUrls.ADMIN_WORKSPACE_USERS(workspaceId))
        return response.data
    } catch (error) {
        console.error("Error getting workspace users:", error)
        showErrorToast(error, "Failed to fetch workspace users")
        throw error
    }
}

export type InternalTeamMember = {
    userId: string
    fullName: string
    email: string
    phoneNumber?: string
    reportingManagerId?: string
    reportingManagerName?: string
}

export type InternalTeamMembersResponse = {
    statusCode: number
    status: string
    message: string
    data: InternalTeamMember[]
}

export type AssignRecruitersToManagerPayload = {
    managerId: string
    recruiterIds: string[]
}

export type AssignRecruitersToManagerResult = {
    managerId: string
    updated: number
    recruiterIds: string[]
}

export type AssignRecruitersToManagerResponse = {
    statusCode: number
    status: string
    message: string
    data: AssignRecruitersToManagerResult
}

export const getWorkspaceTeamManagers = async (workspaceId: string): Promise<InternalTeamMembersResponse> => {
    try {
        const response = await axiosInstance.get(apiUrls.ADMIN_TEAM_MANAGERS(workspaceId))
        return response.data
    } catch (error) {
        console.error("Error getting workspace team managers:", error)
        showErrorToast(error, "Failed to fetch managers")
        throw error
    }
}

export const getWorkspaceTeamRecruiters = async (workspaceId: string): Promise<InternalTeamMembersResponse> => {
    try {
        const response = await axiosInstance.get(apiUrls.ADMIN_TEAM_RECRUITERS(workspaceId))
        return response.data
    } catch (error) {
        console.error("Error getting workspace team recruiters:", error)
        showErrorToast(error, "Failed to fetch recruiters")
        throw error
    }
}

export const assignRecruitersToManager = async (
    workspaceId: string,
    payload: AssignRecruitersToManagerPayload
): Promise<AssignRecruitersToManagerResponse> => {
    try {
        const response = await axiosInstance.put(apiUrls.ADMIN_ASSIGN_RECRUITERS_TO_MANAGER(workspaceId), payload)
        return response.data
    } catch (error) {
        console.error("Error assigning recruiters to manager:", error)
        showErrorToast(error, "Failed to assign recruiters")
        throw error
    }
}

export type LinkUserToWorkspaceItem = {
    userId: string
    roleType: WorkspaceRoleValue
}

export type LinkUserToWorkspacePayload = {
    users: LinkUserToWorkspaceItem[]
}

function getErrorMessageFromPayloadErrors(errors: unknown): string {
    if (typeof errors === "string") return errors
    if (Array.isArray(errors)) {
        const first = errors[0]
        if (typeof first === "string") return first
        if (first && typeof first === "object" && "message" in first)
            return String((first as { message: unknown }).message)
        return errors.length ? String(errors[0]) : "An error occurred"
    }
    if (errors && typeof errors === "object" && "message" in errors)
        return String((errors as { message: unknown }).message)
    return typeof errors === "object" && errors !== null ? JSON.stringify(errors) : String(errors)
}

export const linkUserToWorkspace = async (workspaceId: string, payload: LinkUserToWorkspacePayload) => {
    try {
        const response = await axiosInstance.post(apiUrls.ADMIN_LINK_USER_TO_WORKSPACE(workspaceId), payload)
        const data = response.data as { errors?: unknown }
        if (data?.errors != null) {
            const message = getErrorMessageFromPayloadErrors(data.errors)
            throw new Error(message)
        }
        return response.data
    } catch (error) {
        if (error instanceof Error && error.message) throw error
        console.error("Error linking user to workspace:", error)
        showErrorToast(error, "Failed to link user to workspace")
        throw error
    }
}

export const deleteUser = async (workspaceId: string, userId: string) => {
    try {
        const response = await axiosInstance.delete(apiUrls.ADMIN_DELETE_USER(workspaceId, userId))
        return response.data
    } catch (error) {
        console.error("Error deleting user:", error)
        showErrorToast(error, "Failed to delete user")
        throw error
    }
}

export type EditUserPayload = {
    role: WorkspaceRoleValue
    password?: string
}

export const editUser = async (workspaceId: string, userId: string, payload: EditUserPayload) => {
    try {
        const response = await axiosInstance.put(apiUrls.ADMIN_EDIT_USER(workspaceId, userId), payload)
        return response.data
    } catch (error) {
        console.error("Error editing user:", error)
        showErrorToast(error, "Failed to edit user")
        throw error
    }
}

/** PATCH body: include only keys that changed. Any credit block may include `addons` (e.g. pulse ≥ 1). */
export type PatchWorkspaceCreditsPayload = {
    videoCredits?: CreateWorkspaceCredits
    audioCredits?: CreateWorkspaceCredits
    outboundAudioCredits?: CreateWorkspaceCredits
    chargeAttemptRule?: string
    maxAttempts?: number
}

export const patchWorkspaceCredits = async (workspaceId: string, payload: PatchWorkspaceCreditsPayload) => {
    try {
        const response = await axiosInstance.patch(apiUrls.ADMIN_UPDATE_CREDITS(workspaceId), payload)
        return response.data
    } catch (error) {
        console.error("Error patching credits:", error)
        showErrorToast(error, "Failed to update credits")
        throw error
    }
}

export type GetInternalJobsParams = {
    page?: number
    size?: number
    search?: string
    workspaceId?: string
}

export type InternalJobApi = {
    jobId: string
    jobName: string
    workspaceId?: string
    workspaceName?: string
    status?: string
    expiry?: string
    leadsCount?: number
    assigneeCount?: number
    [key: string]: unknown
}

export type InternalJobsResponseData = {
    content: InternalJobApi[]
    totalElements: number
    totalPages: number
    number: number
    size: number
    first?: boolean
    last?: boolean
    numberOfElements?: number
    empty?: boolean
}

export type InternalJobsResponse = {
    data?: InternalJobsResponseData
    [key: string]: unknown
}

export const listJobs = async (params: GetInternalJobsParams = {}) => {
    try {
        const { page = 0, size = 20, search, workspaceId } = params
        const response = await axiosInstance.get(apiUrls.ADMIN_LIST_JOBS(), {
            params: { page, size, ...(search && { search }), ...(workspaceId && { workspaceId }) },
        })
        return response.data as InternalJobsResponse
    } catch (error) {
        console.error("Error listing jobs:", error)
        showErrorToast(error, "Failed to fetch jobs")
        throw error
    }
}

export type GetJobByIdParams = {
    page?: number
    size?: number
    search?: string
}

// ----------------------------------------------------------------------
// GET /internal/jobs/:jobId - Job leads detail response
// ----------------------------------------------------------------------

export type AdminJobLeadCandidate = {
    candidateId: string
    candidateName: string
    phoneNumber: string
    resumeUrl: string
    aiTag: string
    roundStatus: string
    appliedAt: string
}

export type AdminJobLeadsCandidatesPage = {
    totalElements: number
    totalPages: number
    first: boolean
    last: boolean
    size: number
    content: AdminJobLeadCandidate[]
    number: number
    sort: { empty: boolean; unsorted: boolean; sorted: boolean }
    numberOfElements: number
    pageable: {
        pageNumber: number
        pageSize: number
        sort: { empty: boolean; unsorted: boolean; sorted: boolean }
        offset: number
        unpaged: boolean
        paged: boolean
    }
    empty: boolean
}

export type AdminGetJobLeadsData = {
    jobId: string
    jobName: string
    workspaceId: string
    workspaceName: string
    status: string
    expiry?: string
    candidates: AdminJobLeadsCandidatesPage
}

export type InternalJobDetailResponse = {
    statusCode: number
    status: string
    message: string
    data: AdminGetJobLeadsData
}

export const getLeadsByJobId = async (jobId: string, params: GetJobByIdParams = {}) => {
    try {
        const { page = 0, size = 20, search } = params
        const response = await axiosInstance.get(apiUrls.ADMIN_GET_JOB_LEADS(jobId), {
            params: { page, size, ...(search && { search }) },
        })
        return response.data as InternalJobDetailResponse
    } catch (error) {
        console.error("Error fetching job by id:", error)
        showErrorToast(error, "Failed to fetch job leads")
        throw error
    }
}

export type DeleteJobLeadsPayload = {
    urls: string[]
}

export const deleteJobLeads = async (workspaceId: string, payload: DeleteJobLeadsPayload) => {
    try {
        await axiosInstance.delete(apiUrls.ADMIN_DELETE_JOB_LEADS(workspaceId), {
            data: payload,
        })
    } catch (error) {
        console.error("Error deleting job leads:", error)
        showErrorToast(error, "Failed to delete job leads")
        throw error
    }
}

export type JobAssigneeApi = {
    id: string
    name: string
}

export type JobAssigneesResponse = {
    statusCode: number
    status: string
    message: string
    data: JobAssigneeApi[]
}

export type AssignRecruitersToJobPayload = {
    recruiterIds: string[]
}

export type AssignRecruitersToJobResult = {
    jobId: string
    assigned: number
    recruiterIds: string[]
    assignees: JobAssigneeApi[]
}

export type AssignRecruitersToJobResponse = {
    statusCode: number
    status: string
    message: string
    data: AssignRecruitersToJobResult
}

export const getJobAssignees = async (jobId: string): Promise<JobAssigneesResponse> => {
    try {
        const response = await axiosInstance.get(apiUrls.ADMIN_JOB_ASSIGNEES(jobId))
        return response.data
    } catch (error) {
        console.error("Error getting job assignees:", error)
        showErrorToast(error, "Failed to fetch assigned recruiters")
        throw error
    }
}

export const assignRecruitersToJob = async (
    jobId: string,
    payload: AssignRecruitersToJobPayload
): Promise<AssignRecruitersToJobResponse> => {
    try {
        const response = await axiosInstance.put(apiUrls.ADMIN_JOB_ASSIGNEES(jobId), payload)
        return response.data
    } catch (error) {
        console.error("Error assigning recruiters to job:", error)
        showErrorToast(error, "Failed to assign recruiters")
        throw error
    }
}
